import { AnimatePresence } from 'framer-motion';
import type { HabitWithStreak, HabitCompletion, CoachingCategory } from '@/types';
import { HabitCard } from './HabitCard';
import { CATEGORY_ICONS, ClipboardList } from '@/lib/category-icons';

interface HabitListProps {
  habits: HabitWithStreak[];
  onComplete: (habitId: string, notes?: string, moodRating?: number) => Promise<HabitCompletion>;
  onEdit?: (habit: HabitWithStreak) => void;
  onDelete?: (habitId: string) => void;
  groupByCategory?: boolean;
  emptyMessage?: string;
}

const categoryOrder: CoachingCategory[] = ['faith', 'family', 'finance', 'fitness'];

const categoryLabels: Record<CoachingCategory, { label: string }> = {
  faith: { label: 'Faith' },
  family: { label: 'Family' },
  finance: { label: 'Finance' },
  fitness: { label: 'Fitness' },
};

export function HabitList({
  habits,
  onComplete,
  onEdit,
  onDelete,
  groupByCategory = true,
  emptyMessage = 'No habits yet. Create your first habit to get started!',
}: HabitListProps) {
  if (habits.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-charcoal-800 flex items-center justify-center">
          <ClipboardList className="w-8 h-8 text-slate-500" />
        </div>
        <p className="text-slate-400">{emptyMessage}</p>
      </div>
    );
  }

  if (!groupByCategory) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AnimatePresence>
          {habits.map((habit) => (
            <HabitCard
              key={habit.id}
              habit={habit}
              onComplete={onComplete}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </AnimatePresence>
      </div>
    );
  }

  // Group by category
  const grouped = categoryOrder.reduce((acc, cat) => {
    acc[cat] = habits.filter((h) => h.category === cat);
    return acc;
  }, {} as Record<CoachingCategory, HabitWithStreak[]>);

  return (
    <div className="space-y-8">
      {categoryOrder.map((category) => {
        const categoryHabits = grouped[category];
        if (categoryHabits.length === 0) return null;

        const { label } = categoryLabels[category];
        const Icon = CATEGORY_ICONS[category];

        return (
          <div key={category}>
            <h3 className="flex items-center gap-2 text-lg font-semibold text-white mb-4">
              {Icon && <Icon className="w-5 h-5" />}
              <span>{label}</span>
              <span className="text-sm font-normal text-slate-500">
                ({categoryHabits.filter((h) => h.completedToday).length}/{categoryHabits.length})
              </span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AnimatePresence>
                {categoryHabits.map((habit) => (
                  <HabitCard
                    key={habit.id}
                    habit={habit}
                    onComplete={onComplete}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                ))}
              </AnimatePresence>
            </div>
          </div>
        );
      })}
    </div>
  );
}
